// ============================================================
// Sentry init - T1 / S1-F2
//
// Coleta erros JS nao capturados, falhas de Axios e exceptions
// de componentes React (ErrorBoundary).
//
// IMPORTANTE: a DSN nao fica no codigo - vem da env REACT_APP_SENTRY_DSN.
// Sem DSN, init e no-op (Sentry desabilitado). Isso permite:
//   - dev local sem Sentry: nao polui o painel
//   - homologacao/producao: setar a env e o app fala
//
// Como configurar:
//   1) Criar projeto no Sentry (tipo React)
//   2) Copiar a DSN do projeto (Settings > Client Keys)
//   3) Adicionar em frontend/.env.local (gitignored):
//        REACT_APP_SENTRY_DSN=https://xxx@o123.ingest.sentry.io/456
//        REACT_APP_SENTRY_ENV=development | homologacao | production
//   4) Rebuild do app (CRA so le envs no boot do dev server)
//
// Como verificar:
//   - Apos init, qualquer console.error ou throw vira evento no painel
//   - Pode forcar com: window.__sentryTest = () => Sentry.captureException(new Error('teste'))
// ============================================================
import * as Sentry from '@sentry/react';

let initialized = false;

export const initSentry = () => {
  if (initialized) return;

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Sem DSN configurado - silenciosamente desabilitado.
    // Em dev isso e o esperado; em producao o boot deveria falhar
    // ou logar warning - vamos com warning pra nao bloquear.
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.warn('[Sentry] REACT_APP_SENTRY_DSN nao definida. Erros nao serao reportados.');
    }
    return;
  }

  const environment = process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV || 'development';
  const release = process.env.REACT_APP_VERSION || undefined;

  Sentry.init({
    dsn,
    environment,
    release,

    // 10% de amostra de transacoes em prod (custo).
    // Em dev/homol pode ficar maior pra testar.
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Replays de sessao - so quando ja tem erro
    // (custo zero quando nada quebra).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Filtrar ruido conhecido antes de enviar.
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const msg = (error && error.message) || event?.message || '';

      // Erros do interceptor de logout em cascata (vide services/api.js)
      if (typeof msg === 'string' && msg.includes('Sessao encerrada, redirecionando')) {
        return null;
      }

      // Erros de cancelamento de request (Axios cancelToken)
      if (msg && /canceled|aborted/i.test(msg)) {
        return null;
      }

      return event;
    },

    // Permite agrupar erros por idcliente (sem expor PII).
    initialScope: () => {
      const scope = {};
      try {
        const idcliente = localStorage.getItem('sessionCliente');
        const idusuario = localStorage.getItem('sessionId');
        if (idcliente) scope.tags = { ...(scope.tags || {}), idcliente };
        if (idusuario) scope.user = { id: idusuario };
      } catch {
        // localStorage indisponivel (modo privado, etc.) - nao quebra.
      }
      return scope;
    },
  });

  initialized = true;
  // eslint-disable-next-line no-console
  console.info(`[Sentry] inicializado (env=${environment})`);
};

// Helpers reexportados para o resto do app usar sem importar @sentry/react direto.
export const captureException = (err, ctx) => {
  if (!initialized) return;
  Sentry.captureException(err, ctx);
};

export const setUserContext = ({ idusuario, idcliente, idloja }) => {
  if (!initialized) return;
  Sentry.setUser(idusuario ? { id: String(idusuario) } : null);
  if (idcliente) Sentry.setTag('idcliente', String(idcliente));
  if (idloja) Sentry.setTag('idloja', String(idloja));
};

export const clearUserContext = () => {
  if (!initialized) return;
  Sentry.setUser(null);
};

export const SentryErrorBoundary = Sentry.ErrorBoundary;

export default {
  initSentry,
  captureException,
  setUserContext,
  clearUserContext,
  SentryErrorBoundary,
};
